import { useState } from 'react'

import { PriceMapsScreen, shownError, syncPriceMaps, syncPriceMapsSave, type PriceMapDraft, type PriceType, type PriceTypeMap, type ShownError } from '@krp/sync-ui'

import { queryState, useSyncSources } from './syncKit'

const EMPTY_DRAFT: PriceMapDraft = { firstResourceId: '', firstPriceTypeKey: '', secondResourceId: '', secondPriceTypeKey: '' }

const sameMap = (left: PriceTypeMap, right: PriceTypeMap): boolean =>
  left.firstResourceId === right.firstResourceId &&
  left.firstPriceTypeKey === right.firstPriceTypeKey &&
  left.secondResourceId === right.secondResourceId &&
  left.secondPriceTypeKey === right.secondPriceTypeKey

const sameMaps = (left: readonly PriceTypeMap[], right: readonly PriceTypeMap[]): boolean =>
  left.length === right.length && left.every((map) => right.some((other) => sameMap(map, other)))

/** Вид цены источника уже сопоставлен с другим источником пары (в любую сторону). */
function taken(maps: readonly PriceTypeMap[], resourceId: string, key: string, other: string): boolean {
  return maps.some(
    (map) => (map.firstResourceId === resourceId && map.firstPriceTypeKey === key && map.secondResourceId === other) || (map.secondResourceId === resourceId && map.secondPriceTypeKey === key && map.firstResourceId === other),
  )
}

/** Почему черновую пару нельзя добавить (правила договора `SyncPriceTypeMapsRequest`); `null` — можно. */
function draftProblem(draft: PriceMapDraft, maps: readonly PriceTypeMap[]): string | null {
  if (draft.firstResourceId === '' || draft.secondResourceId === '') return 'Выберите оба источника.'
  if (draft.firstResourceId === draft.secondResourceId) return 'Выберите два разных источника.'
  if (draft.firstPriceTypeKey === '' || draft.secondPriceTypeKey === '') return 'Выберите вид цены у обоих источников.'
  if (taken(maps, draft.firstResourceId, draft.firstPriceTypeKey, draft.secondResourceId)) return 'Этот вид цены первого источника уже сопоставлен со вторым источником.'
  if (taken(maps, draft.secondResourceId, draft.secondPriceTypeKey, draft.firstResourceId)) return 'Этот вид цены второго источника уже сопоставлен с первым источником.'
  return null
}

/** Предложение, оба вида которого ещё свободны с другим источником пары (в том числе среди правок окна). */
const free = (maps: readonly PriceTypeMap[], map: PriceTypeMap): boolean =>
  !taken(maps, map.firstResourceId, map.firstPriceTypeKey, map.secondResourceId) && !taken(maps, map.secondResourceId, map.secondPriceTypeKey, map.firstResourceId)

const NO_TYPES: readonly PriceType[] = []

/**
 * Сопоставление видов цен (RP-SYN8, RP-SYN6, требования 15). Правки — состояние окна; в базу всё сопоставление уходит целиком кнопкой «Сохранить»
 * (`PUT price-type-maps`). Пока правок нет, окно показывает сохранённое службой. Виды цен — из лент цен источников, предложения по одинаковому
 * имени — от службы; добавленное предложение уходит из списка предложений.
 */
export function SyncPriceMapsBinding() {
  const query = syncPriceMaps.useQuery()
  const [save] = syncPriceMapsSave.useMutation()
  const { sources, nameOf } = useSyncSources()
  const [edited, setEdited] = useState<readonly PriceTypeMap[] | null>(null)
  const [draft, setDraft] = useState<PriceMapDraft>(EMPTY_DRAFT)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ShownError | null>(null)
  const [saved, setSaved] = useState(false)

  const stored = query.data?.items ?? []
  const maps = edited ?? stored
  const dirty = edited !== null && !sameMaps(edited, stored)
  const problem = draftProblem(draft, maps)
  const suggestions = (query.data?.suggestions ?? []).filter((suggestion) => free(maps, suggestion))
  const typesOf = (resourceId: string): readonly PriceType[] => query.data?.sources.find((source) => source.resourceId === resourceId)?.priceTypes ?? NO_TYPES
  const typeName = (resourceId: string, key: string): string => typesOf(resourceId).find((type) => type.key === key)?.name ?? key
  const added = (more: readonly PriceTypeMap[]) => {
    setEdited([...maps, ...more])
    setSaved(false)
  }

  const run = () => {
    setBusy(true)
    setError(null)
    void save({ items: [...maps] })
      .unwrap()
      .then(() => {
        setEdited(null)
        setSaved(true)
      })
      .catch((reason: unknown) => setError(shownError('Сопоставление не сохранено', reason)))
      .finally(() => setBusy(false))
  }

  const state = queryState(query, 'Не удалось получить сопоставление видов цен')
  return (
    <PriceMapsScreen
      vm={{
        status: state.status,
        loadError: state.loadError,
        maps: [...maps],
        suggestions,
        sources: sources.map((source) => ({ resourceId: source.resourceId, name: source.displayName })),
        nameOf,
        typesOf,
        typeName,
        draft,
        draftProblem: problem,
        dirty,
        busy,
        error,
        saved,
      }}
      actions={{
        draftChanged: setDraft,
        add: () => {
          added([{ ...draft }])
          setDraft(EMPTY_DRAFT)
        },
        addSuggestion: (index) => {
          const suggestion = suggestions[index]
          if (suggestion !== undefined) added([suggestion])
        },
        addAllSuggestions: () => added(suggestions),
        remove: (index) => {
          setEdited(maps.filter((map) => map !== maps[index]))
          setSaved(false)
        },
        save: run,
        reload: () => void query.refetch(),
      }}
    />
  )
}
